import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { Trash2, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../../dataManagement/uiDb/UiDb";
import {
  createBusiness,
  deleteBusiness,
  updateBusiness,
} from "../../../../services/thunks/BusinessThunk";
import { BusinessProfile, CreateBusinessPayload } from "../../../../interfaces/BusinessObject";
import genericUtility from "../../../../services/utility/GenericUtility";
import { useNotification } from "../../../shared/contexts/NotificationContext";
import { NotificationType } from "../../../../constants/Enums";
import useScrollLock from "../../../../hooks/useScrollLock";
import { useMediaUpload } from "../../../../hooks/useMediaUpload";
import CreateProductConfigHelper from "../../../../services/helpers/CreateProductConfigHelper";
import { makeBusinessFieldPersist } from "../../../../services/helpers/BusinessFieldPersistHelper";
import ConfirmModal from "../../../shared/ConfirmModal/ConfirmModal";
import { ResolvedUser } from "../../../shared/UserByPhoneInput/UserByPhoneInput";
import { useBusinessProfilePicture } from "../../../../hooks/useBusinessProfilePicture";
import BusinessLogoUploader from "./childComponents/BusinessLogoUploader";
import BusinessForm from "./childComponents/BusinessForm";
import LocationPinPicker from "../../../shared/ShareProfileModal/childComponents/LocationPinPicker";
import "./styles/CreateBusinessModal.css";

interface CreateBusinessModalProps {
  isOpen: boolean;
  onClose: () => void;
  editBusiness?: BusinessProfile | null;
}

const buildEmptyForm = (currentUserId: string): CreateBusinessPayload => ({
  name: "",
  listingType: "",
  phone: "",
  description: "",
  collaborators: [],
  ownerId: currentUserId, // "Me" is the default in create mode
  latitude: null,
  longitude: null,
});

const CreateBusinessModal: React.FC<CreateBusinessModalProps> = ({
  isOpen,
  onClose,
  editBusiness = null,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { triggerNotification, startLoading, stopLoading } = useNotification();

  const currentUserId = genericUtility.getCurrentUserId() ?? "";

  const loadingMap = useSelector((state: RootState) => state.business.loadingMap);
  const uiConfigurations = useSelector((state: RootState) => state.general.uiConfigurations);
  const isSubmitting = !!(
    loadingMap.createBusiness || loadingMap.updateBusiness
  );
  const isDeleting = !!loadingMap.deleteBusiness;

  const listingTypeOptions = useMemo(() => {
    const master = CreateProductConfigHelper.getListingTypesMaster(uiConfigurations);
    return master.listing_types
      .filter((lt) => lt.enabled)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((lt) => ({ id: lt.id, label: lt.label }));
  }, [uiConfigurations]);

  const [form, setForm] = useState<CreateBusinessPayload>(buildEmptyForm(currentUserId));
  // Real, backend-persisted logoUrl — no canonical-path guessing, no cache-bust
  // trick needed (each upload gets a genuinely new publicUrl, see
  // useBusinessLogoUpload).
  const existingLogoUrl = useBusinessProfilePicture(editBusiness?.businessId, editBusiness?.logoUrl);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [showPinPicker, setShowPinPicker] = useState(false);

  const isEditMode = !!editBusiness;

  // Who is the effective owner right now? (creator if ownerId is null)
  const effectiveOwnerId =
    editBusiness?.ownerId ?? editBusiness?.creatorId ?? null;
  const isEffectiveOwner = isEditMode && effectiveOwnerId === currentUserId;

  // Pre-fill the "Someone else" chip when editing a business owned by an external user
  const initialResolvedOwner: ResolvedUser | null = useMemo(() => {
    if (!editBusiness?.ownerId || editBusiness.ownerId === currentUserId) return null;
    return {
      userId: editBusiness.ownerId,
      userName: editBusiness.ownerName ?? undefined,
      phone: "",
    };
  }, [editBusiness, currentUserId]);

  useScrollLock(isOpen, overlayRef, ".create-biz-body");

  useEffect(() => {
    if (isOpen && editBusiness) {
      setForm({
        name: editBusiness.name,
        listingType: editBusiness.listingType,
        phone: editBusiness.phone,
        description: editBusiness.description ?? "",
        collaborators: editBusiness.collaborators ?? [],
        ownerId: editBusiness.ownerId, // tri-state preserved as-is
        latitude: editBusiness.latitude ?? null,
        longitude: editBusiness.longitude ?? null,
      });
      setLogoFile(null);
      setShowPinPicker(false);
    } else if (isOpen && !editBusiness) {
      setForm(buildEmptyForm(currentUserId));
      setLogoFile(null);
      setShowPinPicker(false);
    }
  }, [isOpen, editBusiness, currentUserId]);

  const handleFormChange = (patch: Partial<CreateBusinessPayload>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const validate = (): boolean => {
    if (!form.name.trim()) {
      triggerNotification("Business name is required.", NotificationType.ERROR);
      return false;
    }
    if (!form.listingType) {
      triggerNotification("Select a listing type.", NotificationType.ERROR);
      return false;
    }
    if (!/^\d{10}$/.test(form.phone)) {
      triggerNotification("Enter a valid 10-digit business phone number.", NotificationType.ERROR);
      return false;
    }
    return true;
  };

  // Logo upload here is deferred until after the business itself is
  // created/updated — for a brand-new business there's no businessId until
  // creation succeeds, so (unlike useBusinessLogoUpload, which binds a fixed
  // businessId upfront) this uses useMediaUpload directly and resolves the
  // businessId at call time via a ref the persist step reads.
  const pendingLogoBusinessIdRef = useRef<string | null>(null);
  const persistLogo = useCallback(
    makeBusinessFieldPersist(
      dispatch,
      "logoUrl",
      () => pendingLogoBusinessIdRef.current ?? undefined,
      "Uploaded, but couldn't save the logo. Please try again."
    ),
    [dispatch]
  );
  const { upload: uploadLogoFiles } = useMediaUpload({ folder: "business", persist: persistLogo });

  const uploadLogoIfNeeded = async (businessId: string) => {
    if (!logoFile) return;
    pendingLogoBusinessIdRef.current = businessId;
    const batch = await uploadLogoFiles([logoFile]);
    const result = batch.results[0];
    if (!result || result.status !== "success") {
      const detail = result?.message ?? "logo upload failed.";
      triggerNotification(`Business saved, but ${detail}`, NotificationType.ERROR);
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    startLoading(isEditMode ? "Saving changes..." : "Creating business...");
    try {
      if (isEditMode && editBusiness) {
        const payload: any = { businessId: editBusiness.businessId };
        
        // Only include fields that have changed
        if (form.name !== editBusiness.name) payload.name = form.name;
        if (form.listingType !== editBusiness.listingType) payload.listingType = form.listingType;
        if (form.phone !== editBusiness.phone) payload.phone = form.phone;
        if (form.description !== (editBusiness.description ?? "")) payload.description = form.description;
        if (form.latitude !== (editBusiness.latitude ?? null)) payload.latitude = form.latitude;
        if (form.longitude !== (editBusiness.longitude ?? null)) payload.longitude = form.longitude;
        if (form.ownerId !== editBusiness.ownerId) payload.ownerId = form.ownerId;
        if (JSON.stringify(form.collaborators) !== JSON.stringify(editBusiness.collaborators ?? [])) {
          payload.collaborators = form.collaborators;
        }

        const result = await dispatch(updateBusiness(payload)).unwrap();
        await uploadLogoIfNeeded(editBusiness.businessId);

        // If the user transferred ownership away from themselves, close the modal —
        // they no longer have control to keep editing.
        const newEffectiveOwner = result.ownerId ?? result.creatorId;
        if (newEffectiveOwner !== currentUserId) {
          triggerNotification(
            "Ownership transferred. You no longer manage this business.",
            NotificationType.SUCCESS
          );
        } else {
          triggerNotification("Business updated!", NotificationType.SUCCESS);
        }
      } else {
        const created = await dispatch(createBusiness(form)).unwrap();
        await uploadLogoIfNeeded(created.businessId);
        triggerNotification("Business created!", NotificationType.SUCCESS);
      }
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      triggerNotification(msg, NotificationType.ERROR);
    } finally {
      stopLoading();
    }
  };

  const handleConfirmDelete = async () => {
    if (!editBusiness) return;
    try {
      await dispatch(deleteBusiness(editBusiness.businessId)).unwrap();
      triggerNotification("Business deleted.", NotificationType.SUCCESS);
      setConfirmDeleteOpen(false);
      onClose();
      navigate("/my-listings");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Couldn't delete business.";
      triggerNotification(msg, NotificationType.ERROR);
      setConfirmDeleteOpen(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="create-biz-overlay" ref={overlayRef} onClick={onClose}>
        <div className="create-biz-modal" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="create-biz-header">
            <button
              className="create-biz-close"
              onClick={onClose}
              aria-label="Close"
            >
              <X size={20} />
            </button>
            <h2 className="create-biz-title">
              {isEditMode ? "Edit Business" : "Create Business"}
            </h2>
            <div style={{ width: "2.125rem" }} />
          </div>

          {/* Scrollable body */}
          <div className="create-biz-body">
            <BusinessLogoUploader
              existingLogoUrl={existingLogoUrl}
              onFileSelected={setLogoFile}
              onInvalidFile={(reason) => triggerNotification(reason, NotificationType.ERROR)}
            />

            <BusinessForm
              form={form}
              listingTypeOptions={listingTypeOptions}
              listingTypeLocked={isEditMode}
              onChange={handleFormChange}
              currentUserId={currentUserId}
              initialResolvedOwner={initialResolvedOwner}
              onPickLocation={() => setShowPinPicker(true)}
            />

            {/* Delete affordance — effective owner only */}
            {isEditMode && isEffectiveOwner && (
              <button
                type="button"
                className="create-biz-delete-link"
                onClick={() => setConfirmDeleteOpen(true)}
                disabled={isSubmitting}
              >
                <Trash2 size={16} />
                Delete this business
              </button>
            )}
          </div>

          {/* Footer */}
          <div className="create-biz-footer">
            <button
              className="create-biz-submit"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? isEditMode
                  ? "Saving..."
                  : "Creating..."
                : isEditMode
                ? "Save Changes"
                : "Create Business"}
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmDeleteOpen}
        title="Delete this business?"
        message={
          <>
            This will permanently remove <strong>{editBusiness?.name}</strong> and all of its data.
            This cannot be undone.
          </>
        }
        confirmLabel="Delete"
        tone="danger"
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDeleteOpen(false)}
      />

      {showPinPicker &&
        createPortal(
          <div
            className="biz-pin-overlay"
            onClick={() => setShowPinPicker(false)}
          >
            <div
              className="biz-pin-modal"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Intentionally reuses the parent modal's `create-biz-close` /
                  `create-biz-title` styles for a consistent header. If those
                  classes are renamed/restyled, update this sub-modal too. */}
              <div className="biz-pin-header">
                <button
                  className="create-biz-close"
                  onClick={() => setShowPinPicker(false)}
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
                <h2 className="create-biz-title">Set location</h2>
                <div style={{ width: "2.125rem" }} />
              </div>
              <p className="biz-pin-subtitle">
                Drag the pin to your exact business location.
              </p>
              <LocationPinPicker
                initialCoords={
                  typeof form.latitude === "number" &&
                  typeof form.longitude === "number"
                    ? { lat: form.latitude, lng: form.longitude }
                    : null
                }
                onConfirm={(coords) => {
                  handleFormChange({
                    latitude: coords.lat,
                    longitude: coords.lng,
                  });
                  setShowPinPicker(false);
                }}
                onCancel={() => setShowPinPicker(false)}
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default CreateBusinessModal;
